import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit, ExternalLink, Search, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '~/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { apiClient, type Series } from '~/lib/api';

export default function SeriesPage() {
  const queryClient = useQueryClient();
  const [editingSeries, setEditingSeries] = useState<Series | null>(null);
  const [deletingSeries, setDeletingSeries] = useState<Series | null>(null);
  const [formData, setFormData] = useState({ slug: '', name: '', jpName: '' });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-series', page, search],
    queryFn: () => apiClient.getSeries({ page, limit, search }),
  });

  const handleSearchSubmit = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const getSeriesUrl = (slug: string) => {
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://javfacts.com';
    return `${baseUrl}/en/series/${slug}`;
  };

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; slug: string; name: string; jpName?: string }) =>
      apiClient.updateSeries(data.id, { slug: data.slug, name: data.name, jpName: data.jpName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-series'] });
      setEditingSeries(null);
      setFormData({ slug: '', name: '', jpName: '' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteSeries(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-series'] });
      setDeletingSeries(null);
    },
  });

  const handleEdit = (series: Series) => {
    setEditingSeries(series);
    setFormData({
      slug: series.slug,
      name: series.name,
      jpName: series.jpName || '',
    });
  };

  const handleSave = () => {
    if (editingSeries && formData.slug && formData.name) {
      updateMutation.mutate({
        id: editingSeries.id,
        slug: formData.slug,
        name: formData.name,
        jpName: formData.jpName || undefined,
      });
    }
  };

  const handleDelete = () => {
    if (deletingSeries) {
      deleteMutation.mutate(deletingSeries.id);
    }
  };

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-3xl font-bold tracking-tight'>Series Management</h1>
        <p className='text-muted-foreground'>Browse and manage all series in the database</p>
      </div>

      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle>All Series</CardTitle>
              <CardDescription>{data?.data ? `${data.data.total} series total` : 'Loading...'}</CardDescription>
            </div>
            <div className='flex items-center gap-2'>
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  placeholder='Search by slug or name...'
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchSubmit();
                    }
                  }}
                  onBlur={handleSearchSubmit}
                  className='pl-9 w-[300px]'
                />
                {searchInput && (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0'
                    onClick={handleSearchClear}
                  >
                    <X className='h-4 w-4' />
                  </Button>
                )}
              </div>
              {search && (
                <Button variant='secondary' size='sm' onClick={handleSearchClear}>
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='text-center py-8'>
              <p className='text-muted-foreground'>Loading series...</p>
            </div>
          ) : error ? (
            <div className='text-center py-8'>
              <p className='text-destructive'>Error loading series</p>
            </div>
          ) : data?.data?.items.length === 0 ? (
            <div className='text-center py-8'>
              <p className='text-muted-foreground'>{search ? 'No series match your search' : 'No series found'}</p>
              {search && (
                <Button
                  variant='link'
                  onClick={() => {
                    setSearch('');
                    setPage(1);
                  }}
                  className='mt-2'
                >
                  Clear search
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='w-[300px]'>Slug</TableHead>
                      <TableHead>English Name</TableHead>
                      <TableHead>Japanese Name</TableHead>
                      <TableHead>Videos</TableHead>
                      <TableHead className='w-[200px]'>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.data?.items.map((series: any) => (
                      <TableRow key={series.id}>
                        <TableCell className='font-mono text-xs'>{series.slug}</TableCell>
                        <TableCell>
                          <span className='font-medium'>{series.name}</span>
                        </TableCell>
                        <TableCell>
                          {series.jpName ? (
                            <span className='text-sm'>{series.jpName}</span>
                          ) : (
                            <span className='text-muted-foreground text-sm'>N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant='secondary'>{series._count.videos} videos</Badge>
                        </TableCell>
                        <TableCell>
                          <div className='flex items-center gap-2'>
                            <Button variant='ghost' size='sm' onClick={() => handleEdit(series)}>
                              <Edit className='h-4 w-4' />
                            </Button>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => setDeletingSeries(series)}
                              className='text-destructive hover:text-destructive'
                            >
                              <Trash2 className='h-4 w-4' />
                            </Button>
                            <a
                              href={getSeriesUrl(series.slug)}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
                            >
                              <ExternalLink className='h-4 w-4' />
                            </a>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {data?.data && data.data.totalPages > 1 && (
                <div className='flex items-center justify-between mt-4'>
                  <p className='text-sm text-muted-foreground'>
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, data.data.total)} of {data.data.total}{' '}
                    series
                  </p>
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPage((p) => Math.min(data.data.totalPages, p + 1))}
                      disabled={page === data.data.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Sheet */}
      <Sheet open={!!editingSeries} onOpenChange={(open) => !open && setEditingSeries(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit Series</SheetTitle>
            <SheetDescription>Update series information</SheetDescription>
          </SheetHeader>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='slug'>
                Slug <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='slug'
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder='series-slug'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='name'>
                English Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='name'
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder='Series Name'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='jpName'>Japanese Name</Label>
              <Input
                id='jpName'
                value={formData.jpName}
                onChange={(e) => setFormData({ ...formData, jpName: e.target.value })}
                placeholder='シリーズ名'
              />
            </div>
          </div>
          <SheetFooter>
            <Button variant='outline' onClick={() => setEditingSeries(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!formData.slug || !formData.name || updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingSeries} onOpenChange={(open) => !open && setDeletingSeries(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Series</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "<strong>{deletingSeries?.name}</strong>"?
              {deletingSeries && deletingSeries._count.videos > 0 && (
                <span className='block mt-2 text-destructive font-semibold'>
                  This series has {deletingSeries._count.videos} associated videos and cannot be deleted.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deletingSeries?._count.videos! > 0 || deleteMutation.isPending}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
